import { memo, useCallback, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from '../../../constants/pagination';
import './pagination.css';

export interface PaginationProps {
  total: number;
  /** Zero-based page index. */
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  rowsPerPageOptions?: readonly number[];
  /** `dark` matches the customer app; `light` (default) matches the dashboard. */
  tone?: 'light' | 'dark';
  className?: string;
}

// Inline SVG: the Material Symbols font only loads inside the dashboard.
const Chevron = ({ direction }: { direction: 'left' | 'right' }) => (
  <svg className="pagination-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d={direction === 'left' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} />
  </svg>
);

/**
 * The one pagination footer for every paged list (dashboard and customer
 * app): rows-per-page selector, "start–end of total", prev/next.
 *
 * Hidden while everything fits on the smallest page size — at that point
 * there is nothing to page and no smaller size to switch back to.
 */
export const Pagination = memo(function Pagination({
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  tone = 'light',
  className = '',
}: PaginationProps) {
  const { t } = useTranslation();
  const selectId = useId();

  // Keep the active size selectable even if a caller's default isn't listed.
  const options = useMemo(
    () =>
      rowsPerPageOptions.includes(rowsPerPage)
        ? rowsPerPageOptions
        : [...rowsPerPageOptions, rowsPerPage].sort((a, b) => a - b),
    [rowsPerPageOptions, rowsPerPage],
  );

  const handlePrev = useCallback(() => onPageChange(page - 1), [onPageChange, page]);
  const handleNext = useCallback(() => onPageChange(page + 1), [onPageChange, page]);
  const handleRowsChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => onRowsPerPageChange(Number(e.target.value)),
    [onRowsPerPageChange],
  );

  if (total <= options[0]) return null;

  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const start = page * rowsPerPage + 1;
  const end = Math.min((page + 1) * rowsPerPage, total);

  return (
    <nav
      className={`pagination pagination--${tone} ${className}`.trim()}
      aria-label={t('pagination.aria')}
    >
      <div className="pagination-rows">
        <label htmlFor={selectId} className="pagination-label">
          {t('pagination.rowsPerPage')}:
        </label>
        <select
          id={selectId}
          value={rowsPerPage}
          onChange={handleRowsChange}
          className="pagination-select"
        >
          {options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
      <div className="pagination-info">
        <span className="pagination-text" aria-live="polite">
          {t('pagination.range', { start, end, total })}
        </span>
        <div
          className="pagination-buttons"
          role="group"
          aria-label={t('pagination.controlsAria')}
        >
          <button
            type="button"
            onClick={handlePrev}
            disabled={page === 0}
            className="pagination-btn"
            aria-label={t('pagination.previousPage')}
          >
            <Chevron direction="left" />
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={page >= totalPages - 1}
            className="pagination-btn"
            aria-label={t('pagination.nextPage')}
          >
            <Chevron direction="right" />
          </button>
        </div>
      </div>
    </nav>
  );
});

export default Pagination;
